/**
 * Runtime configuration (M1.14.1).
 *
 * Fetched from `/config.json` rather than baked into the bundle, and that is
 * the whole point: the API's URL is written by the **app** stack, which is
 * destroyed and rebuilt at alpha, while the client is served from the
 * **persistent** stack and survives ([D22](../../../Design-Decisions.md)). A
 * compiled-in URL would mean a web release after every rebuild of the backend.
 *
 * In development the file does not exist, so the values come from Vite's env —
 * which is how `npm run dev` points at the deployed alpha API.
 */

export interface RuntimeConfig {
  readonly envName: string;
  readonly region: string;
  readonly apiBaseUrl: string;
  readonly userPoolId: string;
  readonly webClientId: string;
}

export class ConfigUnavailableError extends Error {
  constructor(readonly detail: string) {
    super(detail);
    this.name = 'ConfigUnavailableError';
  }
}

let cached: RuntimeConfig | undefined;

/**
 * Reads the configuration once per page load.
 *
 * Cached because everything that needs it needs it early, and a second fetch
 * would race the first on a cold load.
 */
export async function loadConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<RuntimeConfig> {
  if (cached !== undefined) {
    return cached;
  }

  const fromEnv = configFromEnv();
  if (fromEnv !== undefined) {
    cached = fromEnv;
    return cached;
  }

  let response: Response;
  try {
    // `cache: no-cache` rather than `no-store`: the file is tiny and changes
    // when the API is rebuilt, so a revalidation on each load is right and a
    // stale copy would point at an API that no longer exists.
    response = await fetchImpl('/config.json', { cache: 'no-cache' });
  } catch (error) {
    throw new ConfigUnavailableError(
      `The application could not load its configuration: ${String(error)}`,
    );
  }

  if (!response.ok) {
    throw new ConfigUnavailableError(
      `The application's configuration is missing (${String(response.status)}). ` +
        'This usually means the backend has not been deployed.',
    );
  }

  const parsed = (await response.json()) as Partial<RuntimeConfig>;

  for (const key of ['apiBaseUrl', 'userPoolId', 'webClientId', 'region'] as const) {
    if (typeof parsed[key] !== 'string' || parsed[key] === '') {
      throw new ConfigUnavailableError(`The configuration is missing "${key}".`);
    }
  }

  cached = {
    envName: parsed.envName ?? 'unknown',
    region: parsed.region as string,
    // A trailing slash here produces `//items` in every request path.
    apiBaseUrl: (parsed.apiBaseUrl as string).replace(/\/+$/, ''),
    userPoolId: parsed.userPoolId as string,
    webClientId: parsed.webClientId as string,
  };

  return cached;
}

/** For tests, which must not inherit one another's configuration. */
export function resetConfigForTests(next?: RuntimeConfig): void {
  cached = next;
}

function configFromEnv(): RuntimeConfig | undefined {
  // Index-signature access, because `noPropertyAccessFromIndexSignature` is on
  // and Vite types `import.meta.env` as one.
  const env = import.meta.env;
  const apiBaseUrl = env['VITE_API_BASE_URL'];

  if (typeof apiBaseUrl !== 'string' || apiBaseUrl === '') {
    return undefined;
  }

  return {
    envName: env['VITE_ENV_NAME'] ?? 'dev',
    region: env['VITE_REGION'] ?? 'us-east-1',
    apiBaseUrl: apiBaseUrl.replace(/\/+$/, ''),
    userPoolId: env['VITE_USER_POOL_ID'] ?? '',
    webClientId: env['VITE_WEB_CLIENT_ID'] ?? '',
  };
}
